// /usage — user-facing diagnostic page.
//
// Four sections, top to bottom:
//   1. PlanSummaryCard — current plan, dates, days-left, Shop CTA on trial.
//   2. PortalQuotaMeters — org-wide Overall Clients + Response Generation meters.
//   3. FeatureFlagsTable — customer-relevant capability checklist.
//   4. OrderHistoryTable — caller's own org orders, click-to-detail.
//
// Open to every logged-in user (tenant_admin + regular). Reachable from
// the navbar "Usage" link and also stays reachable for trial-expired
// users via the ExpiredGuard / OnboardingGuard allow-list — seeing what
// they've consumed strengthens the Shop CTA on the same page.

import { useEffect } from "react";
import { Button, Space, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useSubscriptionStatus } from "../../hooks/useSubscriptionStatus";
import PlanSummaryCard from "./PlanSummaryCard";
import PortalQuotaMeters from "./PortalQuotaMeters";
import FeatureFlagsTable from "./FeatureFlagsTable";
import OrderHistoryTable from "./OrderHistoryTable";

const { Title, Paragraph } = Typography;

export default function UsagePage() {
  const navigate = useNavigate();
  const { status, isLoading, refetch } = useSubscriptionStatus();

  // Force a fresh fetch every time the user lands here. The hook
  // memoises the response in a module-level cache (so the Navbar
  // pill / ExpiredGuard don't refetch on every render); without this
  // explicit refetch the user would have to hard-refresh to see the
  // updated `used_responses` counter after generating a response on
  // some other page in the same SPA session.
  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={2} style={{ marginBottom: 4, color: "#0a1e54" }}>
            Usage
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Your current plan, what you have used so far this period, and your
            past orders. All figures update live as you and your team work in
            the product.
          </Paragraph>
        </div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/")}
          style={{ flexShrink: 0 }}
        >
          Dashboard
        </Button>
      </div>

      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PlanSummaryCard status={status} isLoading={isLoading} />
        <PortalQuotaMeters status={status} isLoading={isLoading} />
        <FeatureFlagsTable />
        <OrderHistoryTable />
      </Space>
    </div>
  );
}
